import { FeatureKey, Rights } from '../../_models/feature';

/** Rights per tab id, in the shape `ecpl-tab-navigation` takes as `linkAccess`. */
export type EmployeeTabRights = { id: number; access: boolean }[];

/**
 * The Employees list tabs, before rights are applied. Shared by the Employees
 * page and the hover sub-navigation so both list the same tabs.
 * @param children Children of the Employees feature node.
 * @param roleId Current role id (`BaseServices.roleId`).
 */
export function employeeListTabs(
  children: any[] | null | undefined,
  roleId: string
): any[] {
  let allTabs = [...(children ?? [])];
  const documentedTraining = allTabs.filter(
    (e: any) => e.translationKey == 'DOCUMENTED_TRAINING'
  );
  if (documentedTraining.length > 0) {
    allTabs = allTabs.filter(
      (e: any) => e.translationKey != 'DOCUMENTED_TRAINING'
    );
    allTabs.push(documentedTraining[0]);
  }
  let tabs = allTabs.filter(
    (element: any) =>
      (element.checked || element.IsDefault) &&
      element.id !== FeatureKey.EMPLOYEE_WORK_EXPERIENCE
  );
  if (roleId == '5') {
    tabs = allTabs.filter(
      (element: any) =>
        (element.checked || element.IsDefault) &&
        element.id !== FeatureKey.EMPLOYEE_WORK_EXPERIENCE &&
        element.id !== FeatureKey.EMPLOYEE_COURSE_DETAILS
    );
  }
  if (roleId == '4') {
    tabs = tabs.filter(
      (x: any) =>
        x.id != FeatureKey.APPROVAL_DOCUMENTS &&
        x.id != FeatureKey.DOCUMENTED_TRAINING &&
        x.id != 203
    );
  }
  if (roleId === '2') {
    tabs = tabs.map((element: any) =>
      element.id === FeatureKey.EMPLOYEES
        ? { ...element, translationKey: 'CUSTOMER_EMPLOYEES' }
        : element
    );
  }
  return tabs;
}

/**
 * The link for an Employees tab. Document tabs carry the employee id in their path.
 * @param tab Employees tab.
 * @param employeeId The employee in view, or 0 on the list.
 */
export function employeeTabLinkText(tab: any, employeeId: number): string {
  if (tab.name == 'DocumentedTraining' || tab.name == 'Documented Training') {
    return employeeId > 0
      ? 'documented-training/multi-doc/' + employeeId
      : 'documented-training/multi-doc/' + employeeId + '/1';
  }
  if (tab.name == 'EmployeesPrivateDocuments') {
    return 'private-doc/multi-doc/' + employeeId + '/1';
  }
  return tab.linkText;
}

/**
 * Which Employees tabs the role may open, or undefined when every tab is allowed.
 * @param role Current user role (`BaseServices.UserRole`).
 * @param hasRight Whether the user holds a right; consulted for the User role.
 */
export function employeeTabRights(
  role: string,
  hasRight: (rightId: number) => boolean
): EmployeeTabRights | undefined {
  if (role === 'User') {
    return [
      { id: FeatureKey.EMPLOYEE_LEAVE, access: hasRight(Rights.APPROVE_LEAVE) },
      {
        id: FeatureKey.EMPLOYEE_SICKNESS_ABSENCE,
        access: hasRight(Rights.APPROVE_ABSENSE)
      },
      {
        id: FeatureKey.EMPLOYEE_TIMETABLE,
        access: hasRight(Rights.APPROVE_TIMETABLE)
      },
      {
        id: FeatureKey.DOCUMENTED_TRAINING,
        access: hasRight(Rights.DOCUMENTED_TRAINING_RIGHTS)
      },
      {
        id: FeatureKey.APPROVAL_DOCUMENTS,
        access: hasRight(Rights.APPROVAL_DOCUMENT_RIGHTS)
      }
    ];
  }
  if (role === 'Guest') {
    return [
      { id: FeatureKey.EMPLOYEE_LEAVE, access: false },
      { id: FeatureKey.EMPLOYEE_SICKNESS_ABSENCE, access: false },
      { id: FeatureKey.EMPLOYEE_TIMETABLE, access: false },
      { id: FeatureKey.EMPLOYEE_COURSE_DETAILS, access: false },
      { id: FeatureKey.EMPLOYEE_FIRST_OF_KIN, access: false }
    ];
  }
  if (role === 'Portal') {
    return [
      { id: FeatureKey.EMPLOYEE_LEAVE, access: false },
      { id: FeatureKey.EMPLOYEE_SICKNESS_ABSENCE, access: true },
      { id: FeatureKey.EMPLOYEE_TIMETABLE, access: false },
      { id: FeatureKey.EMPLOYEE_FIRST_OF_KIN, access: false }
    ];
  }
  if (role === 'Admin' || role === 'Editor') {
    return [
      { id: FeatureKey.EMPLOYEE_LEAVE, access: true },
      { id: FeatureKey.EMPLOYEE_SICKNESS_ABSENCE, access: true },
      { id: FeatureKey.EMPLOYEE_TIMETABLE, access: true },
      { id: FeatureKey.EMPLOYEE_COURSE_DETAILS, access: true },
      { id: FeatureKey.EMPLOYEE_FIRST_OF_KIN, access: true }
    ];
  }
  return undefined;
}
